import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Badge, EmptyState, Switch, TableSkeleton, TagInput } from "../../../components/ui";
import { getAlerts, testAlerts, updateAlerts } from "../../../services/alerts";
import { can } from "../../../utils/roles";

const RULES = {
  paymentFailures: { help: "Failed payments in one hour, from", unit: "failures" },
  mobileMoneyDown: { help: "Mobile Money failures in two hours with no success, from", unit: "failures" },
  noPayments: { help: "People tried to pay (3 attempts by 2 people at least) and none succeeded, over", unit: "hours", field: "hours" },
  jwplayer: { help: "The JW Player API does not answer or refuses the key (video upload)." },
  serverErrors: { help: "Server errors in one hour, from", unit: "errors" },
  mailQuota: { help: "Share of the monthly email quota used, from", unit: "%" },
};
const when = (date) => (date ? new Date(date).toLocaleString([], { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : null);

/** Email alerts to the team: rules with their threshold, recipients, test, history. */
const Alerts = () => {
  const { t } = useTranslation();
  const canEdit = can("logs", "edit");
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () =>
    getAlerts()
      .then((result) => {
        setData(result);
        setDraft({ enabled: result.enabled, recipients: result.recipients, rules: Object.fromEntries(result.rules.map((r) => [r.key, { enabled: r.enabled, threshold: r.threshold, hours: r.hours }])) });
      })
      .catch(() => toast.error(t("Error fetching data")));
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!data || !draft) return <section className="ui-card"><TableSkeleton rows={5} /></section>;

  const setRule = (key, changes) => setDraft((d) => ({ ...d, rules: { ...d.rules, [key]: { ...d.rules[key], ...changes } } }));
  const save = async () => {
    setBusy(true);
    try {
      const result = await updateAlerts(draft);
      setData(result);
      toast.success(t("Alerts saved"));
    } catch (error) {
      toast.error(error?.response?.data?.message || t("Error updating data"));
    } finally {
      setBusy(false);
    }
  };
  const test = async () => {
    setBusy(true);
    try {
      const result = await testAlerts();
      if (result.sent) toast.success(result.triggered.length ? t("Alert sent: {{list}}", { list: result.triggered.map((a) => a.label).join(", ") }) : t("Test email sent: no problem right now"));
      else toast.error(result.error || t("The email could not be sent"));
      load();
    } catch (error) {
      toast.error(error?.response?.data?.message || t("Error updating data"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <section className="ui-card alerts-card">
        <div className="alerts-head">
          <div>
            <h2 className="alerts-title">{t("Email alerts")}</h2>
            <p className="ui-hint">
              {t("Checked every 15 minutes on the production server. The same alert is not sent again for {{hours}} hours.", { hours: data.cooldownHours })}
              {data.lastCheckAt ? ` ${t("Last check: {{date}}", { date: when(data.lastCheckAt) })}` : ""}
            </p>
          </div>
          <Switch checked={draft.enabled} onChange={(enabled) => canEdit && setDraft((d) => ({ ...d, enabled }))} label={t("Alerts")} onLabel={t("On")} offLabel={t("Off")} />
        </div>

        <ul className="alerts-rules">
          {data.rules.map((rule) => {
            const meta = RULES[rule.key] || {};
            const value = draft.rules[rule.key] || {};
            const field = meta.field || "threshold";
            return (
              <li key={rule.key} data-off={!value.enabled || undefined}>
                <Switch checked={Boolean(value.enabled)} onChange={(enabled) => canEdit && setRule(rule.key, { enabled })} label={t(rule.label)} />
                <div className="alerts-rule-text">
                  <strong>{t(rule.label)}</strong>
                  <span className="ui-hint">
                    {t(meta.help)}
                    {meta.unit && (
                      <>
                        {" "}
                        <input
                          className="ui-input alerts-threshold"
                          type="number"
                          min={field === "hours" ? 2 : 1}
                          max={field === "hours" ? 168 : 10000}
                          aria-label={t(rule.label)}
                          disabled={!canEdit}
                          value={value[field] ?? ""}
                          onChange={(e) => setRule(rule.key, { [field]: e.target.value })}
                        />{" "}
                        {t(meta.unit)}
                      </>
                    )}
                  </span>
                </div>
                {rule.lastSentAt && <Badge color="amber">{t("Last sent {{date}}", { date: when(rule.lastSentAt) })}</Badge>}
              </li>
            );
          })}
        </ul>

        <div className="ui-field">
          <label htmlFor="alerts-recipients">{t("Recipients")}</label>
          <TagInput id="alerts-recipients" value={draft.recipients} onChange={(recipients) => setDraft((d) => ({ ...d, recipients }))} placeholder={t("Email address, then Enter")} />
        </div>

        {canEdit && (
          <div className="alerts-actions">
            <button type="button" className="ui-btn ui-btn-secondary" disabled={busy} onClick={test}>
              <i className="ri-mail-send-line" aria-hidden="true"></i>
              {t("Check now and send a test")}
            </button>
            <button type="button" className="ui-btn" disabled={busy} onClick={save}>
              {t("Save")}
            </button>
          </div>
        )}
      </section>

      <section className="ui-card alerts-card">
        <h2 className="alerts-title">{t("Recent alerts")}</h2>
        {!data.history.length ? (
          <EmptyState icon="ri-checkbox-circle-line">{t("No alert so far")}</EmptyState>
        ) : (
          <ul className="alerts-history">
            {data.history.map((item, i) => (
              <li key={`${item.at}-${i}`}>
                <span className="ui-hint">{when(item.at)}</span>
                <Badge color={item.rule === "test" ? "slate" : "red"}>{item.rule === "test" ? t("Test") : t(data.rules.find((r) => r.key === item.rule)?.label || item.rule)}</Badge>
                <span>{item.detail}</span>
                {!item.sent && <Badge color="amber">{t("not sent")}{item.error ? ` : ${item.error}` : ""}</Badge>}
              </li>
            ))}
          </ul>
        )}
      </section>
    </>
  );
};

export default Alerts;
